import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import {
  ICS235_BOX_LABELS,
  ICS235_ELEMENT_ROWS,
  ICS235_FIRST_SPARE_COLUMN,
  ICS235_REQUIREMENT_COLUMNS,
  ICS235_SPARE_ROW,
} from '../ics235BoxLabels';
import {
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
} from './newCgFormBoxes';

type Facilities = {
  spareColumns?: string[];
  spareRow?: { location?: string; element?: string };
};

type Matrix = Record<string, Record<string, string>>;

/** The sheet's last row, which totals the columns above it. */
const ICS235_TOTAL_ROW = ICS235_ELEMENT_ROWS.length - 1;

/**
 * Fill in the Total Needed row from the rows above it.
 *
 * The total is derived rather than typed, but it is written back into the
 * stored matrix so the PDF and Word exports keep printing it straight out of
 * the cell map without needing to know how it is worked out.
 */
function withColumnTotals(matrix: Matrix): Matrix {
  const totals: Record<string, string> = {};

  ICS235_REQUIREMENT_COLUMNS.forEach((_, column) => {
    let sum = 0;
    let counted = false;

    for (let row = 0; row < ICS235_TOTAL_ROW; row += 1) {
      const raw = String(matrix[String(row)]?.[String(column)] ?? '').trim();
      if (raw === '') continue;
      const value = Number(raw);
      if (Number.isNaN(value)) continue;
      sum += value;
      counted = true;
    }

    totals[String(column)] = counted ? String(sum) : '';
  });

  return { ...matrix, [String(ICS235_TOTAL_ROW)]: totals };
}

/**
 * ICS 235-CG, boxed as the sheet prints it.
 *
 * Boxes 4, 5 and 6 are the three headings of one grid — twelve ICS-element
 * rows against twenty-five requirement columns — so they render as a single
 * box. The trailing three columns and the eleventh row are blank on the sheet
 * for an incident to name, and those titles are stored alongside the values.
 */
const Ics235Card = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: rows = [] } = useAuthoredRows('facilityAssessments');
  const createRow = useCreateAuthoredRow('facilityAssessments');
  const updateRow = useUpdateAuthoredRow('facilityAssessments');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_235',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();

  const assessment = rows[0] ?? null;
  const facilities = (assessment?.facilities as Facilities) ?? {};
  const spareColumns = facilities.spareColumns ?? [];
  const spareRow = facilities.spareRow ?? {};
  const matrix = withColumnTotals((assessment?.matrix as Matrix) ?? {});

  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftLocation, setDraftLocation] = useState('');
  const [draftElement, setDraftElement] = useState('');

  const persist = (patch: Record<string, unknown>) => {
    if (!assessment) {
      createRow.mutate({ facilities: {}, matrix: {}, ...patch }, { onSuccess });
      return;
    }
    updateRow.mutate(
      { entityId: String(assessment.assessment_id), ...patch },
      { onSuccess }
    );
  };

  const saveFacilities = (patch: Facilities) =>
    persist({ facilities: { ...facilities, ...patch } });

  const beginEdit = (row: number) => {
    setEditingRow(row);
    setDraft({ ...(matrix[String(row)] ?? {}) });
    setDraftLocation(spareRow.location ?? '');
    setDraftElement(spareRow.element ?? '');
  };

  const commitEdit = (row: number) => {
    const patch: Record<string, unknown> = {
      matrix: withColumnTotals({ ...matrix, [String(row)]: draft }),
    };
    if (row === ICS235_SPARE_ROW) {
      patch.facilities = {
        ...facilities,
        spareRow: { location: draftLocation, element: draftElement },
      };
    }
    persist(patch);
    setEditingRow(null);
  };

  const columnLabel = (index: number) =>
    index < ICS235_FIRST_SPARE_COLUMN
      ? ICS235_REQUIREMENT_COLUMNS[index]
      : (spareColumns[index - ICS235_FIRST_SPARE_COLUMN] ?? '');

  return (
    <NewCgFormCard
      title="Facility Needs Assessment ICS-235"
      formname="ICS_235_CG"
      baseId="ICS_235"
      exportLabel="Preview ICS-235"
    >
      <NewCgHeaderBoxes labels={ICS235_BOX_LABELS} testIdPrefix="ics235" />

      <Ics204CardSection
        boxLabel={ICS235_BOX_LABELS.needsGrid}
        testId="ics235-section-needs-grid"
        className="tw:mt-0"
      >
        <div className="tw:overflow-x-auto" data-testid="ics235-needs-table">
          <table className="tw:w-full tw:text-xs">
            <thead>
              <tr>
                <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                  Location
                </th>
                <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground">
                  ICS Element
                </th>
                {ICS235_REQUIREMENT_COLUMNS.map((_, column) => (
                  <th key={column} className="tw:px-1 tw:py-1 tw:align-bottom">
                    {column < ICS235_FIRST_SPARE_COLUMN ? (
                      <span className="tw:block tw:w-24 tw:font-normal tw:text-muted-foreground">
                        {columnLabel(column)}
                      </span>
                    ) : (
                      <Input
                        className="tw:w-24"
                        aria-label={`Requirement ${column - ICS235_FIRST_SPARE_COLUMN + 1}`}
                        placeholder={`Requirement ${column - ICS235_FIRST_SPARE_COLUMN + 1}`}
                        defaultValue={columnLabel(column)}
                        onBlur={(event) => {
                          const value = event.target.value;
                          if (value === columnLabel(column)) return;
                          const next = [...spareColumns];
                          next[column - ICS235_FIRST_SPARE_COLUMN] = value;
                          saveFacilities({ spareColumns: next });
                        }}
                      />
                    )}
                  </th>
                ))}
                <th className="tw:w-24" />
              </tr>
            </thead>
            <tbody>
              {ICS235_ELEMENT_ROWS.map((element, row) => {
                const isTotal = row === ICS235_TOTAL_ROW;
                const isEditing = editingRow === row;
                const rowName =
                  row === ICS235_SPARE_ROW
                    ? spareRow.element || 'the additional element'
                    : element.element;

                return (
                  <tr key={row}>
                    <td className="tw:px-1 tw:py-1 tw:whitespace-nowrap">
                      {row === ICS235_SPARE_ROW && isEditing ? (
                        <Input
                          className="tw:w-20"
                          aria-label="Additional location"
                          value={draftLocation}
                          onChange={(event) =>
                            setDraftLocation(event.target.value)
                          }
                        />
                      ) : row === ICS235_SPARE_ROW ? (
                        spareRow.location || '—'
                      ) : (
                        element.location
                      )}
                    </td>
                    <td className="tw:px-1 tw:py-1 tw:whitespace-nowrap">
                      {row === ICS235_SPARE_ROW && isEditing ? (
                        <Input
                          className="tw:w-48"
                          aria-label="Additional ICS element"
                          value={draftElement}
                          onChange={(event) =>
                            setDraftElement(event.target.value)
                          }
                        />
                      ) : row === ICS235_SPARE_ROW ? (
                        spareRow.element || '—'
                      ) : (
                        element.element
                      )}
                    </td>
                    {ICS235_REQUIREMENT_COLUMNS.map((_, column) => {
                      const current =
                        matrix[String(row)]?.[String(column)] ?? '';
                      const cellName =
                        columnLabel(column) ||
                        `Requirement ${column - ICS235_FIRST_SPARE_COLUMN + 1}`;

                      return (
                        <td key={column} className="tw:px-1 tw:py-1">
                          {isEditing ? (
                            <Input
                              className="tw:w-24"
                              aria-label={`${rowName} ${cellName}`}
                              value={draft[String(column)] ?? ''}
                              onChange={(event) =>
                                setDraft((state) => ({
                                  ...state,
                                  [String(column)]: event.target.value,
                                }))
                              }
                            />
                          ) : (
                            <span
                              className={`tw:block tw:w-24 tw:px-2 tw:py-1 ${isTotal ? 'tw:font-medium' : ''}`}
                            >
                              {current || '—'}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="tw:px-1 tw:py-1">
                      <div className="tw:flex tw:justify-end tw:gap-1">
                        {isTotal ? null : isEditing ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Cancel editing ${rowName}`}
                              onClick={() => setEditingRow(null)}
                            >
                              <X className="tw:size-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Save ${rowName}`}
                              onClick={() => commitEdit(row)}
                            >
                              <Check className="tw:size-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${rowName}`}
                            onClick={() => beginEdit(row)}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS235_BOX_LABELS.comments}
        testId="ics235-section-comments"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics235-comments"
          label="Comments"
          hideLabel
          multiline
          placeholder="Enter comments..."
          value={String(assessment?.comments ?? '')}
          onSave={(value) => persist({ comments: value })}
        />
      </Ics204CardSection>

      {!assessment ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="tw:self-start"
          onClick={() => persist({})}
        >
          Start assessment
        </Button>
      ) : null}

      <NewCgPreparedByBox
        baseId="ICS_235"
        boxLabel={ICS235_BOX_LABELS.preparedBy}
        testIdPrefix="ics235"
      />
    </NewCgFormCard>
  );
};

export default Ics235Card;
